import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Divider,
  Alert,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { useSnackbar } from '../../../components/SnackbarProvider';
import axiosInstance from '../../../lib/axios';
import { useCanViewFinancials } from '../../../hooks/usePermissions';
import type {
  Supplier,
  Location,
  ProductSupplierPrice,
} from '../../../types';

interface CreateBulkRestockOrderDto {
  supplierId: string;
  locationId: string;
  expectedDeliveryDate?: string;
  notes?: string;
  items: {
    productId: string;
    quantity: number;
    unitCost: number;
  }[];
}

interface BulkRestockOrder {
  id: string;
  orderNumber: string;
  status: string;
}

interface RestockFromSupplierModalProps {
  open: boolean;
  onClose: () => void;
  productId: string;
  productName: string;
  supplierId?: string; // Pre-selected supplier (locked if provided)
  supplierName?: string;
  suggestedPrice?: number;
  currentStock?: number;
}

export const RestockFromSupplierModal = ({
  open,
  onClose,
  productId,
  productName,
  supplierId: lockedSupplierId,
  supplierName,
  suggestedPrice,
  currentStock = 0,
}: RestockFromSupplierModalProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const canViewCosts = useCanViewFinancials();

  const [supplierId, setSupplierId] = useState(lockedSupplierId || '');
  const [locationId, setLocationId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unitCost, setUnitCost] = useState(suggestedPrice?.toString() || '');
  const [notes, setNotes] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setSupplierId(lockedSupplierId || '');
      setUnitCost(suggestedPrice?.toString() || '');
      setQuantity('');
      setNotes('');
    }
  }, [open, lockedSupplierId, suggestedPrice]);

  // Update unitCost when selected supplier changes (fetch their last price)
  useEffect(() => {
    if (supplierId && !lockedSupplierId && open) {
      // Fetch supplier price for this product if exists
      axiosInstance
        .get(`/products/${productId}/supplier-prices`)
        .then((res) => {
          const price = (res.data as ProductSupplierPrice[]).find(
            (p) => p.supplierId === supplierId
          );
          if (price) {
            setUnitCost(price.unitCost.toString());
          }
        })
        .catch(() => {});
    }
  }, [supplierId, productId, lockedSupplierId, open]);

  // Fetch available suppliers
  const { data: suppliers, isLoading: loadingSuppliers } = useQuery({
    queryKey: ['suppliers', 'active'],
    queryFn: async () => {
      const response = await axiosInstance.get('/suppliers', {
        params: { isActive: true, limit: 100 },
      });
      return response.data.items || [];
    },
    enabled: !lockedSupplierId && open,
  });

  // Fetch user's locations
  const { data: locations, isLoading: loadingLocations } = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const response = await axiosInstance.get('/locations');
      // Handle paginated response: { data: Location[], total: number }
      return response.data.data || response.data || [];
    },
    enabled: open,
  });

  // Set default location when locations load
  useEffect(() => {
    if (locations && locations.length > 0 && !locationId) {
      setLocationId(locations[0].id);
    }
  }, [locations, locationId]);

  const restockMutation = useMutation({
    mutationFn: async (data: CreateBulkRestockOrderDto) => {
      const response = await axiosInstance.post('/restocks', data);
      return response.data as BulkRestockOrder;
    },
    onSuccess: (order) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['restocks'] });
      queryClient.invalidateQueries({ queryKey: ['product-supplier-prices', productId] });
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      showMessage(
        `Commande ${order.orderNumber} créée avec succès. Statut: Devis`,
        'success'
      );
      onClose();
      // Navigate to the new order
      navigate(`/app/restocks/${order.id}`);
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.message || "Erreur lors de la création de la commande";
      showMessage(message, 'error');
    },
  });

  const handleSubmit = () => {
    // For users without cost visibility, unitCost will use suggestedPrice or default to 0
    const effectiveUnitCost = canViewCosts ? unitCost : (suggestedPrice?.toString() || '0');

    if (!supplierId || !locationId || !quantity) {
      showMessage('Veuillez remplir tous les champs obligatoires', 'error');
      return;
    }

    if (canViewCosts && !unitCost) {
      showMessage('Veuillez remplir le prix unitaire', 'error');
      return;
    }

    // Create a BulkRestockOrder with a single item instead of direct inventory movement
    const data: CreateBulkRestockOrderDto = {
      supplierId,
      locationId,
      notes: notes || undefined,
      items: [
        {
          productId,
          quantity: parseInt(quantity),
          unitCost: parseFloat(effectiveUnitCost),
        },
      ],
    };

    restockMutation.mutate(data);
  };

  const totalCost =
    quantity && unitCost ? parseInt(quantity) * parseFloat(unitCost) : 0;

  const isLoading = restockMutation.isPending;
  // For users without cost visibility, don't require unitCost input
  const isFormValid = supplierId && locationId && quantity && (canViewCosts ? unitCost : true);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocalShippingIcon color="primary" />
        Réapprovisionner - {productName}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Supplier Selection */}
          {lockedSupplierId ? (
            <TextField
              label="Fournisseur"
              value={supplierName || 'Fournisseur sélectionné'}
              fullWidth
              disabled
              InputProps={{ readOnly: true }}
            />
          ) : (
            <FormControl fullWidth required disabled={loadingSuppliers}>
              <InputLabel>Fournisseur</InputLabel>
              <Select
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
                label="Fournisseur"
              >
                {(suppliers || []).map((supplier: Supplier) => (
                  <MenuItem key={supplier.id} value={supplier.id}>
                    {supplier.name} ({supplier.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Location Selection */}
          <FormControl fullWidth required disabled={loadingLocations}>
            <InputLabel>Emplacement</InputLabel>
            <Select
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              label="Emplacement"
            >
              {(locations || []).map((location: Location) => (
                <MenuItem key={location.id} value={location.id}>
                  {location.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Quantity */}
          <TextField
            label="Quantité"
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            fullWidth
            required
            inputProps={{ min: 1 }}
          />

          {/* Unit Cost - only visible to users with financials permission */}
          {canViewCosts && (
            <TextField
              label="Prix unitaire"
              type="number"
              value={unitCost}
              onChange={(e) => setUnitCost(e.target.value)}
              fullWidth
              required
              InputProps={{
                endAdornment: <Typography color="text.secondary">DZD</Typography>,
              }}
              inputProps={{ min: 0, step: 0.01 }}
            />
          )}

          {/* Notes */}
          <TextField
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="Notes sur ce réapprovisionnement..."
          />

          {/* Summary */}
          <Divider />
          <Box
            sx={{
              p: 2,
              bgcolor: 'action.hover',
              borderRadius: 1,
            }}
          >
            {/* Total cost - only visible to users with financials permission */}
            {canViewCosts && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Total commande:
                </Typography>
                <Typography variant="h6" fontWeight={600} color="primary">
                  {totalCost.toLocaleString()} DZD
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Stock actuel:
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {currentStock} unités
              </Typography>
            </Box>
          </Box>

          {/* Info alert about order workflow */}
          <Alert severity="info" sx={{ mt: 1 }}>
            Une commande fournisseur sera créée avec le statut "Devis".
            Le stock sera mis à jour lorsque la commande sera marquée comme livrée.
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading || !isFormValid}
          startIcon={isLoading ? <CircularProgress size={16} /> : <LocalShippingIcon />}
        >
          {isLoading ? 'Création...' : 'Créer la commande'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RestockFromSupplierModal;
